/**
 * PointCloudViewer - Three.js Point Cloud Visualization
 *
 * Renders 3D point clouds from D4RT 4D video analysis.
 * Uses @react-three/fiber and @react-three/drei for React integration.
 *
 * Pattern source: src/components/athlete-visualization.js
 */

import React, { useRef, useMemo, useEffect } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, PerspectiveCamera, Grid } from '@react-three/drei';
import * as THREE from 'three';

/**
 * PointCloud Component
 * Renders points with color based on depth/confidence
 */
const PointCloud = ({ points, colorMode = 'depth' }) => {
  const pointsRef = useRef();

  // Generate geometry and colors
  const { positions, colors } = useMemo(() => {
    if (!points || points.length === 0) {
      // Generate demo point cloud (baseball field)
      return generateDemoPointCloud();
    }

    const positions = new Float32Array(points.length * 3);
    const colors = new Float32Array(points.length * 3);

    points.forEach((point, i) => {
      // Position (x, y, z)
      positions[i * 3] = point.x || 0;
      positions[i * 3 + 1] = point.y || 0;
      positions[i * 3 + 2] = point.z || 0;

      // Color based on mode
      let color;
      if (colorMode === 'depth') {
        // Depth-based coloring (blue=close, red=far)
        const depth = point.z || 0;
        const normalized = Math.min(Math.max((depth - 10) / 20, 0), 1);
        color = new THREE.Color().setHSL(0.6 - normalized * 0.6, 1.0, 0.5);
      } else if (colorMode === 'confidence') {
        // Confidence-based coloring (red=low, green=high)
        const conf = point.confidence || 0.5;
        color = new THREE.Color().setHSL(conf * 0.3, 1.0, 0.5);
      } else {
        // Use point's RGB if available
        color = new THREE.Color(
          (point.r || 128) / 255,
          (point.g || 128) / 255,
          (point.b || 128) / 255
        );
      }

      colors[i * 3] = color.r;
      colors[i * 3 + 1] = color.g;
      colors[i * 3 + 2] = color.b;
    });

    return { positions, colors };
  }, [points, colorMode]);

  // Update geometry when data changes
  useEffect(() => {
    if (pointsRef.current) {
      pointsRef.current.geometry.attributes.position.needsUpdate = true;
      pointsRef.current.geometry.attributes.color.needsUpdate = true;
    }
  }, [positions, colors]);

  return (
    <points ref={pointsRef}>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          count={positions.length / 3}
          array={positions}
          itemSize={3}
        />
        <bufferAttribute
          attach="attributes-color"
          count={colors.length / 3}
          array={colors}
          itemSize={3}
        />
      </bufferGeometry>
      <pointsMaterial
        size={0.05}
        vertexColors
        sizeAttenuation
        transparent
        opacity={0.8}
      />
    </points>
  );
};

/**
 * Generate demo point cloud for baseball field
 */
function generateDemoPointCloud() {
  const positions = [];
  const colors = [];

  // Baseball mound at center
  for (let i = 0; i < 100; i++) {
    const theta = Math.random() * Math.PI * 2;
    const r = Math.random() * 2;
    const x = Math.cos(theta) * r;
    const z = Math.sin(theta) * r;
    const y = Math.random() * 0.5;

    positions.push(x, y, z);

    // Brown color for mound
    colors.push(0.6, 0.4, 0.2);
  }

  // Home plate area
  for (let i = 0; i < 50; i++) {
    const x = (Math.random() - 0.5) * 1;
    const z = 18.44 + (Math.random() - 0.5) * 2; // 60.5 feet = 18.44m
    const y = Math.random() * 0.2;

    positions.push(x, y, z);

    // White color for home plate
    colors.push(0.9, 0.9, 0.9);
  }

  // Field grass
  for (let i = 0; i < 200; i++) {
    const x = (Math.random() - 0.5) * 30;
    const z = Math.random() * 25;
    const y = Math.random() * 0.1;

    positions.push(x, y, z);

    // Green color for grass
    const greenShade = 0.3 + Math.random() * 0.2;
    colors.push(0.1, greenShade, 0.1);
  }

  return {
    positions: new Float32Array(positions),
    colors: new Float32Array(colors),
  };
}

/**
 * Trajectory Line Component
 * Renders tracked point trajectory as a line with confidence-based color coding
 */
const TrajectoryLine = ({ trajectory }) => {
  const { points, colors } = useMemo(() => {
    if (!trajectory || trajectory.length === 0) return { points: [], colors: [] };

    const pts = [];
    const cols = [];

    trajectory.forEach((item, idx) => {
      // Supports multiple backend response shapes:
      // 1) [frameIdx, {u,v}] (mock track_point)
      // 2) {frame,u,v,confidence} (production track_point)
      // 3) {position_3d:{x,y,z}, position_2d:{u,v}, confidence}
      const frameIdx = Array.isArray(item) ? Number(item[0]) : Number(item?.frame ?? item?.frame_index ?? idx);
      const point = Array.isArray(item) ? (item[1] || {}) : (item || {});
      const point2D = point.position_2d || point.point || point;
      const point3D = point.position_3d || point;

      let x = Number(point3D.x);
      let y = Number(point3D.y);
      let z = Number(point3D.z);

      // If 3D coordinates aren't available, project 2D trajectory into a
      // pseudo-3D lane so users still see motion in the static viewer.
      if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) {
        const rawU = Number(point2D.u);
        const rawV = Number(point2D.v);
        const uNorm = Number.isFinite(rawU) ? (rawU > 1 ? rawU / 1920 : rawU) : 0.5;
        const vNorm = Number.isFinite(rawV) ? (rawV > 1 ? rawV / 1080 : rawV) : 0.5;

        x = (Math.max(0, Math.min(1, uNorm)) - 0.5) * 18;
        y = (0.5 - Math.max(0, Math.min(1, vNorm))) * 10;
        z = (Number.isFinite(frameIdx) ? frameIdx : idx) * 0.15;
      }

      pts.push(new THREE.Vector3(x, y, z));

      // Color based on confidence (red=low, cyan=high)
      const confidenceRaw = Number(point.confidence ?? point2D.confidence ?? point3D.confidence ?? 0.8);
      const confidence = Number.isFinite(confidenceRaw) ? confidenceRaw : 0.8;
      const color = new THREE.Color();
      if (confidence > 0.7) {
        color.setHex(0x00ffff); // Cyan for high confidence
      } else if (confidence > 0.4) {
        color.setHex(0xffff00); // Yellow for medium confidence
      } else {
        color.setHex(0xff0000); // Red for low confidence
      }
      cols.push(color.r, color.g, color.b);
    });

    return { points: pts, colors: new Float32Array(cols) };
  }, [trajectory]);

  const lineGeometry = useMemo(() => {
    if (points.length === 0) return null;
    const geo = new THREE.BufferGeometry().setFromPoints(points);
    geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    return geo;
  }, [points, colors]);

  useEffect(() => {
    return () => {
      if (lineGeometry) lineGeometry.dispose();
    };
  }, [lineGeometry]);

  if (!lineGeometry) return null;

  return (
    <line geometry={lineGeometry}>
      <lineBasicMaterial attach="material" vertexColors linewidth={3} />
    </line>
  );
};

/**
 * PointCloudViewer Component
 *
 * @param {Object} props
 * @param {Array} props.points - Array of {x, y, z, confidence, r?, g?, b?}
 * @param {Array} props.trajectory - Array of [frameIdx, {x, y, z}] for tracking
 * @param {string} props.colorMode - 'depth' | 'confidence' | 'rgb'
 * @param {boolean} props.showGrid - Whether to show ground grid
 * @param {boolean} props.autoRotate - Whether to auto-rotate camera
 */
const PointCloudViewer = ({
  points = [],
  trajectory = [],
  colorMode = 'depth',
  showGrid = true,
  autoRotate = false,
}) => {
  return (
    <div className="w-full h-full min-h-[400px] bg-zinc-950 rounded-lg overflow-hidden border border-cyan-500/30">
      <Canvas>
        {/* Camera */}
        <PerspectiveCamera makeDefault position={[10, 10, 20]} fov={50} />

        {/* Lighting */}
        <ambientLight intensity={0.5} />
        <pointLight position={[10, 10, 10]} intensity={1} />
        <pointLight position={[-10, 5, -10]} intensity={0.5} />

        {/* Controls */}
        <OrbitControls
          autoRotate={autoRotate}
          autoRotateSpeed={2}
          enableDamping
          dampingFactor={0.05}
          minDistance={5}
          maxDistance={50}
        />

        {/* Ground Grid */}
        {showGrid && (
          <Grid
            args={[30, 30]}
            cellSize={1}
            cellThickness={0.5}
            cellColor="#444444"
            sectionSize={5}
            sectionThickness={1}
            sectionColor="#666666"
            fadeDistance={50}
            fadeStrength={1}
            followCamera={false}
            position={[0, 0, 0]}
          />
        )}

        {/* Point Cloud */}
        <PointCloud points={points} colorMode={colorMode} />

        {/* Trajectory Line */}
        {trajectory.length > 0 && <TrajectoryLine trajectory={trajectory} />}

        {/* Axes Helper (for debugging) */}
        {/* <axesHelper args={[5]} /> */}
      </Canvas>

      {/* Legend Overlay */}
      <div className="absolute bottom-4 left-4 bg-black/70 backdrop-blur-sm rounded-lg p-3 text-xs border border-cyan-500/30">
        <div className="font-semibold text-cyan-400 mb-2">3D View Controls</div>
        <div className="space-y-1 text-zinc-300">
          <div>• Left click + drag: Rotate</div>
          <div>• Right click + drag: Pan</div>
          <div>• Scroll: Zoom in/out</div>
        </div>
        {colorMode === 'depth' && (
          <div className="mt-2 pt-2 border-t border-zinc-700">
            <div className="text-zinc-400">Color: Depth (blue=close, red=far)</div>
          </div>
        )}
        {colorMode === 'confidence' && (
          <div className="mt-2 pt-2 border-t border-zinc-700">
            <div className="text-zinc-400">Color: Confidence (red=low, green=high)</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PointCloudViewer;
